/**
 * Documentation page
 *
 * In-app user guide for the web app. Content is organized into searchable,
 * collapsible sections so users can quickly learn how to use each feature.
 * All content is bundled in the app - no backend call required.
 */

import { useState, useMemo } from 'react'
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
} from '@mui/material'
import {
  Search,
  ExpandMore,
  RocketLaunch,
  SpaceDashboard,
  Inventory2,
  Build,
  EventRepeat,
  Inventory,
  Notifications,
  AdminPanelSettings,
  HelpOutline,
  MenuBook,
} from '@mui/icons-material'

const PRIMARY = '#1B5E7A'

// ---------------------------------------------------------------------------
// Documentation content
// Each section: { id, icon, title, color, items: [{ subtitle?, text?, bullets? }] }
// ---------------------------------------------------------------------------
const SECTIONS = [
  {
    id: 'getting-started',
    icon: <RocketLaunch />,
    title: 'Getting Started',
    color: '#1B5E7A',
    items: [
      {
        text: 'Sign in with the username and password provided by your administrator. If you forget your password, use the "Forgot Password" link on the login screen or contact your administrator.',
      },
      {
        subtitle: 'Finding your way around',
        text: 'The left sidebar groups every area of the system. Click a top-level item to open it, or expand a group (such as Inventory or Maintenance) to see its pages. The top bar shows notifications and your account menu.',
      },
      {
        text: 'What you can see and do depends on the role assigned to your account. If a menu item is missing, you may not have permission for it - contact your administrator to request access.',
      },
    ],
  },
  {
    id: 'dashboard',
    icon: <SpaceDashboard />,
    title: 'Dashboard',
    color: '#2196F3',
    items: [
      {
        text: 'The dashboard gives a real-time overview of the whole system - work orders, assets, maintenance compliance, and alerts.',
      },
      {
        subtitle: 'Reading the dashboard',
        bullets: [
          'KPI cards summarize key counts; alert metrics are color-coded by severity.',
          'Charts break down work orders by priority and asset health.',
          'Trend graphs compare current activity with previous periods.',
          'Click a card or quick action to jump to the underlying records.',
        ],
      },
    ],
  },
  {
    id: 'assets',
    icon: <Inventory2 />,
    title: 'Assets & Equipment',
    color: '#4CAF50',
    items: [
      {
        text: 'The Asset Register (under Inventory) lists every piece of equipment the system tracks.',
      },
      {
        subtitle: 'Working with assets',
        bullets: [
          'Use search and filters to narrow the list by name, tag, status, location, or criticality.',
          'Click a row to open the asset and view its details, maintenance history, and documents.',
          'With the right permissions you can add, edit, transfer, or retire assets, and print an asset summary.',
          'Each asset has a QR code that can be scanned from the mobile app for instant lookup.',
        ],
      },
      {
        subtitle: 'Related pages',
        text: 'Transfers, Requisitions, External Repairs, Vehicles, and Fault Reports are all found under the Inventory group in the sidebar.',
      },
    ],
  },
  {
    id: 'work-orders',
    icon: <Build />,
    title: 'Work Orders',
    color: '#FF9800',
    items: [
      {
        text: 'Work orders track maintenance jobs from request through completion. Open them from Maintenance > Work Orders.',
      },
      {
        subtitle: 'Creating a work order',
        bullets: [
          'Click "New Work Order" and fill in the title, description, related asset, and priority.',
          'Set a due date and assign a technician (if you have permission).',
          'Save to submit - assigned users are notified.',
        ],
      },
      {
        subtitle: 'Tracking progress',
        bullets: [
          'Open a work order to update its status, add notes, log parts used, and attach files.',
          'Move it through Start and Complete as work proceeds.',
          'Completed work orders can be verified/closed and printed as a job record.',
        ],
      },
    ],
  },
  {
    id: 'maintenance',
    icon: <EventRepeat />,
    title: 'Preventive Maintenance',
    color: '#00ACC1',
    items: [
      {
        text: 'Preventive maintenance (PM) keeps equipment healthy through scheduled, recurring tasks. The Maintenance group includes PM Schedules, the PM Dashboard, Calibrations, and Job Cards.',
      },
      {
        subtitle: 'Staying compliant',
        bullets: [
          'The PM Dashboard shows upcoming, due, and overdue tasks at a glance.',
          'Completing a PM records the work and rolls the schedule forward to its next due date.',
          'Calibrations track instruments that must be calibrated on a fixed cycle; overdue items are flagged.',
        ],
      },
    ],
  },
  {
    id: 'inventory',
    icon: <Inventory />,
    title: 'Spare Parts & Inventory',
    color: '#795548',
    items: [
      {
        text: 'The Inventory module tracks spare parts and consumables, including quantities, categories, and issue vouchers.',
      },
      {
        subtitle: 'Managing stock',
        bullets: [
          'View on-hand quantities and reorder levels for each item.',
          'Record receipts and issues to keep stock counts accurate.',
          'Issue Vouchers document parts handed out for a job and can be printed.',
          'Low-stock and out-of-stock items are highlighted on the dashboard.',
        ],
      },
    ],
  },
  {
    id: 'notifications',
    icon: <Notifications />,
    title: 'Notifications',
    color: '#E91E63',
    items: [
      {
        text: 'The bell icon in the top bar shows recent alerts such as new assignments, approvals, and overdue tasks.',
      },
      {
        bullets: [
          'Open Notifications from the sidebar to review the full history.',
          'Click a notification to open the related record.',
          'Administrators can send broadcast notifications to users from Notifications > Broadcasts.',
        ],
      },
    ],
  },
  {
    id: 'reports',
    icon: <MenuBook />,
    title: 'Reports & Exports',
    color: '#3F51B5',
    items: [
      {
        text: 'The Reports section provides summaries and analytics across assets and maintenance activity.',
      },
      {
        bullets: [
          'Most list pages can be exported (e.g. to PDF) for printing or sharing.',
          'Printable views are branded and ready to save or hand off.',
        ],
      },
    ],
  },
  {
    id: 'administration',
    icon: <AdminPanelSettings />,
    title: 'Administration',
    color: '#607D8B',
    items: [
      {
        text: 'Administrators manage the system under the Administration group.',
      },
      {
        subtitle: 'Common admin tasks',
        bullets: [
          'Users - create accounts, assign roles, and deactivate users.',
          'Roles - control what each role can view and do (permissions).',
          'Sections/Units - organize the structure of your facility.',
          'Audit Logs - review who did what and when for accountability.',
        ],
      },
      {
        text: 'These pages are only visible to administrators.',
      },
    ],
  },
  {
    id: 'troubleshooting',
    icon: <HelpOutline />,
    title: 'Troubleshooting',
    color: '#9C27B0',
    items: [
      {
        subtitle: 'A page says "Permission denied"',
        text: 'Your role does not include access to that feature. Contact your administrator to request the required permission.',
      },
      {
        subtitle: 'Data is not loading',
        bullets: [
          'Check your internet connection.',
          'Refresh the page in your browser.',
          'Sign out and back in if the issue persists.',
        ],
      },
      {
        subtitle: 'Still need help?',
        text: 'Contact your system administrator or the maintenance department for account, permission, or data issues.',
      },
    ],
  },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const matchesQuery = (section, q) => {
  if (!q) return true
  if (section.title.toLowerCase().includes(q)) return true
  return section.items.some((it) => {
    if (it.subtitle?.toLowerCase().includes(q)) return true
    if (it.text?.toLowerCase().includes(q)) return true
    if (it.bullets?.some((b) => b.toLowerCase().includes(q))) return true
    return false
  })
}

const DocItem = ({ item }) => (
  <Box sx={{ mb: 2 }}>
    {item.subtitle && (
      <Typography sx={{ fontWeight: 600, color: PRIMARY, mb: 0.5 }}>
        {item.subtitle}
      </Typography>
    )}
    {item.text && (
      <Typography sx={{ color: '#444', lineHeight: 1.6 }}>{item.text}</Typography>
    )}
    {item.bullets && (
      <Box component="ul" sx={{ pl: 3, m: 0, mt: item.text ? 1 : 0 }}>
        {item.bullets.map((b, i) => (
          <Typography
            key={i}
            component="li"
            sx={{ color: '#444', lineHeight: 1.6, mb: 0.5 }}
          >
            {b}
          </Typography>
        ))}
      </Box>
    )}
  </Box>
)

const Documentation = () => {
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState('getting-started')

  const q = query.trim().toLowerCase()
  const isSearching = q.length > 0

  const filtered = useMemo(
    () => SECTIONS.filter((s) => matchesQuery(s, q)),
    [q]
  )

  const handleChange = (id) => (_event, isExpanded) => {
    setExpanded(isExpanded ? id : false)
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <MenuBook sx={{ color: PRIMARY, fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#212B36' }}>
          Documentation
        </Typography>
      </Box>
      <Typography sx={{ color: '#637381', mb: 3 }}>
        Learn how to use the web app. Expand a topic below or search to jump
        straight to what you need.
      </Typography>

      <TextField
        fullWidth
        placeholder="Search help topics"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 3, backgroundColor: '#fff', borderRadius: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: '#919EAB' }} />
            </InputAdornment>
          ),
        }}
      />

      {filtered.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: '1px solid rgba(145,158,171,0.2)',
            borderRadius: 2,
          }}
        >
          <HelpOutline sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
          <Typography sx={{ color: '#919EAB' }}>
            No topics match "{query}"
          </Typography>
        </Paper>
      ) : (
        filtered.map((section) => (
          <Accordion
            key={section.id}
            expanded={isSearching || expanded === section.id}
            onChange={handleChange(section.id)}
            disableGutters
            elevation={0}
            sx={{
              mb: 1.5,
              border: '1px solid rgba(145,158,171,0.2)',
              borderRadius: 2,
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 1.5,
                },
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: section.color + '22',
                  color: section.color,
                }}
              >
                {section.icon}
              </Box>
              <Typography sx={{ fontWeight: 600, color: '#212B36' }}>
                {section.title}
              </Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ pt: 2 }}>
              {section.items.map((item, i) => (
                <DocItem key={i} item={item} />
              ))}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Typography sx={{ color: '#919EAB', textAlign: 'center', mt: 3 }}>
        Need more help? Contact your system administrator.
      </Typography>
    </Container>
  )
}

export default Documentation
